import React, { useState, useEffect, useCallback } from "react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useConfig } from "../context/ConfigContext";
import { enqueue } from "../lib/offline";
import MonthSelector from "../components/MonthSelector";
import { MONTHS_IT, daysInMonth, firstWeekday, WEEKDAYS_IT } from "../lib/season";
import { X, Users, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function Coperti() {
  const { canWrite } = useAuth();
  const { config } = useConfig();
  const [month, setMonth] = useState(6);
  const [covers, setCovers] = useState({});
  const [day, setDay] = useState(null);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const stdCrew = config?.standard_crew ?? 6;

  const load = useCallback(() => {
    api.get("/covers", { params: { month: `2026-${String(month).padStart(2, "0")}` } })
      .then((r) => {
        const map = {};
        r.data.forEach((c) => { map[c.date] = c; });
        setCovers(map);
      }).catch(() => {});
  }, [month]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const h = () => load();
    window.addEventListener("data-synced", h);
    return () => window.removeEventListener("data-synced", h);
  }, [load]);

  const openDay = (d) => {
    const dateStr = `2026-${String(month).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
    const existing = covers[dateStr];
    setDay(dateStr);
    setForm(existing
      ? { ...existing }
      : { date: dateStr, crew: stdCrew, guests: 0, guests_half: 0, note: "" });
  };

  const save = async () => {
    setSaving(true);
    const payload = { ...form, crew: Number(form.crew), guests: Number(form.guests), guests_half: Number(form.guests_half) };
    try {
      if (!navigator.onLine) {
        await enqueue("cover", payload);
        toast.success("Salvato offline");
      } else {
        await api.post("/covers", payload);
        toast.success("Coperti salvati");
      }
      setDay(null);
      load();
    } catch (e) {
      await enqueue("cover", payload);
      toast.warning("Rete assente: salvato offline");
      setDay(null);
    } finally { setSaving(false); }
  };

  const nDays = daysInMonth(2026, month);
  const offset = firstWeekday(2026, month);
  const monthTotal = Object.values(covers).reduce((s, c) => s + (c.total || 0), 0);

  return (
    <div className="space-y-5">
      <div>
        <h1 className="font-head text-3xl font-bold tracking-tight text-navy">Coperti giornalieri</h1>
        <p className="text-slate-500">{MONTHS_IT[month]} 2026 · <span className="font-semibold text-navy">{monthTotal}</span> coperti totali · equipaggio standard {stdCrew}</p>
      </div>

      <MonthSelector month={month} onChange={setMonth} />

      <div className="rounded-xl border border-slate-200 bg-white p-3 sm:p-5">
        <div className="mb-2 grid grid-cols-7 gap-1 sm:gap-2">
          {WEEKDAYS_IT.map((w) => <div key={w} className="py-1 text-center text-xs font-semibold text-slate-400">{w}</div>)}
        </div>
        <div className="grid grid-cols-7 gap-1 sm:gap-2">
          {Array.from({ length: offset }).map((_, i) => <div key={`e${i}`} />)}
          {Array.from({ length: nDays }).map((_, i) => {
            const d = i + 1;
            const dateStr = `2026-${String(month).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
            const c = covers[dateStr];
            return (
              <button
                key={d}
                data-testid={`day-${d}`}
                onClick={() => canWrite ? openDay(d) : null}
                className={`flex aspect-square flex-col items-center justify-center rounded-lg border text-sm transition-colors ${
                  c ? "border-gold bg-gold/10" : "border-slate-200 bg-white hover:border-slate-300"
                } ${canWrite ? "cursor-pointer" : "cursor-default"}`}
              >
                <span className="tnum font-semibold text-navy">{d}</span>
                {c && <span className="tnum mt-0.5 rounded bg-navy px-1.5 text-[11px] font-bold text-white">{c.total}</span>}
              </button>
            );
          })}
        </div>
      </div>

      {day && form && (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/50 sm:items-center">
          <div className="animate-slide-up w-full max-w-sm rounded-t-2xl bg-white p-6 sm:rounded-2xl">
            <div className="mb-4 flex items-center justify-between">
              <h3 className="font-head text-xl font-bold text-navy">Coperti {day.split("-").reverse().join("/")}</h3>
              <button onClick={() => setDay(null)}><X className="h-6 w-6 text-slate-400" /></button>
            </div>
            <div className="space-y-4">
              {[["crew", "Equipaggio"], ["guests", "Ospiti (interi)"], ["guests_half", "Ospiti mezzo coperto"]].map(([k, l]) => (
                <div key={k}>
                  <label className="mb-1 block text-sm font-semibold text-navy">{l}</label>
                  <div className="flex items-center gap-3">
                    <button onClick={() => setForm({ ...form, [k]: Math.max(0, Number(form[k]) - 1) })} className="h-12 w-12 rounded-lg bg-slate-100 text-2xl font-bold text-navy">−</button>
                    <input data-testid={`cover-${k}`} type="number" value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} className="tnum h-12 flex-1 rounded-lg border-2 border-slate-200 text-center text-lg outline-none focus:border-gold" />
                    <button onClick={() => setForm({ ...form, [k]: Number(form[k]) + 1 })} className="h-12 w-12 rounded-lg bg-slate-100 text-2xl font-bold text-navy">+</button>
                  </div>
                </div>
              ))}
              <div className="rounded-lg bg-navy/5 p-3 text-center">
                <span className="text-sm text-slate-500">Totale coperti: </span>
                <span className="tnum font-head text-xl font-bold text-navy">{Number(form.crew) + Number(form.guests) + 0.5 * Number(form.guests_half)}</span>
              </div>
            </div>
            <button data-testid="save-cover-btn" onClick={save} disabled={saving} className="mt-5 flex h-14 w-full items-center justify-center gap-2 rounded-lg bg-gold font-bold text-black hover:bg-gold-hover disabled:opacity-60">
              {saving && <Loader2 className="h-5 w-5 animate-spin" />} Salva
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
